// This is the ONLY file in the codebase that calls window.gtag().
// All analytics events must go through trackEvent / trackPageView / identifyUser.
// Never call window.gtag() directly from components or hooks.
//
// Active P0 events (frontend):
//   page_view, login_succeeded, login_failed,
//   form_started,
//   registration_started, registration_submitted, registration_failed,
//   user_registered,
//   email_verification_sent, email_verification_completed, email_verification_failed,
//   terms_viewed, terms_accepted, terms_rejected,
//   terms_gate_viewed, terms_gate_accepted,
//   email_gate_viewed,
//   dashboard_viewed_first_time,
//   plan_selected, patient_created, patient_import_started,
//   appointment_created, report_exported,
//   api_error_encountered
//
// Active P0 events (backend — GA4 Measurement Protocol, app/analytics/):
//   invoice_created, patient_import_completed, invite_accepted
//
// Normalized error codes — ALL error_code values in trackEvent() calls MUST use ErrorCode.*
// to prevent free-form strings accumulating in GA4 over time.
export const ErrorCode = {
    DUPLICATE_EMAIL:    'duplicate_email',
    DUPLICATE_ORG:      'duplicate_org',
    INVALID_PHONE:      'invalid_phone',
    WEAK_PASSWORD:      'weak_password',
    WRONG_CREDENTIALS:  'wrong_credentials',
    SERVER_ERROR:       'server_error',
    EMAIL_NOT_VERIFIED: 'email_not_verified',
    VALIDATION_ERROR:   'validation_error',
    PASSWORD_MISMATCH:  'password_mismatch',
    UNKNOWN:            'unknown',
} as const;

// Use ErrorCode type for all error_code params — enforces the allowlist above.
export type ErrorCode = typeof ErrorCode[keyof typeof ErrorCode];

declare global {
    interface Window {
        gtag?: (...args: unknown[]) => void;
        dataLayer?: unknown[];
    }
}

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID ?? '';

// Enabled when REACT_APP_GA_MEASUREMENT_ID is set (any env).
// Leave the env var empty in .env.development to disable in dev.
// Set it to the real ID in .env.development to test in GA4 DebugView.
const IS_ENABLED = GA_ID.length > 0;

type EventParams = Record<string, string | number | boolean | string[] | undefined>;

// Safety net: drop any param key that looks like PII before sending.
// Call sites must still be reviewed — this is a last-resort guard, not a substitute.
const PII_BLOCKED_KEYS = new Set([
    'email', 'password', 'phone', 'name', 'patient_id',
    'user_email', 'national_id', 'birth_date', 'notes',
    'patient_name', 'staff_name', 'doctor_name', 'org_name',
]);

const sanitizeParams = (params: EventParams): EventParams => {
    const safe: EventParams = {};
    for (const [key, value] of Object.entries(params)) {
        if (PII_BLOCKED_KEYS.has(key.toLowerCase())) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn(`[Analytics] Blocked PII param: "${key}". Remove it from the call site.`);
            }
            continue;
        }
        safe[key] = value;
    }
    return safe;
};

export const trackEvent = (eventName: string, params: EventParams = {}): void => {
    if (!IS_ENABLED) {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('[Analytics] GA EVENT (disabled):', eventName, params);
        }
        return;
    }
    const safe = sanitizeParams(params);
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[Analytics] GA EVENT:', eventName, safe);
    }
    try {
        window.gtag?.('event', eventName, safe);
    } catch (e) {
        // Never let analytics errors affect the user experience
        console.error('[Analytics] Failed to fire event:', eventName, e);
    }
};

export const trackPageView = (path: string): void => {
    if (!IS_ENABLED) {
        if (process.env.NODE_ENV !== 'production') {
            console.debug('[Analytics] GA EVENT (disabled): page_view', { page_path: path });
        }
        return;
    }
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[Analytics] GA EVENT: page_view', { page_path: path });
    }
    try {
        window.gtag?.('event', 'page_view', { page_path: path });
    } catch (e) {
        console.error('[Analytics] Failed to fire page_view:', path, e);
    }
};

// Set GA4 user_id (opaque UUID — never email) and persistent user properties.
// Call once after login, when principal.user.id is first available.
export const identifyUser = (userId: string, role: string, locale: string): void => {
    if (!IS_ENABLED) return;
    try {
        window.gtag?.('config', GA_ID, { user_id: userId });
        window.gtag?.('set', 'user_properties', { user_role: role, locale });
    } catch (e) {
        console.error('[Analytics] Failed to identify user:', e);
    }
};
